/**
 * DOM Utility Functions
 *
 * Helper functions for creating and manipulating DOM elements.
 *
 * @package MultilingualPressZone
 * @since 1.0.0
 */

/**
 * Create a DOM element with attributes and children
 *
 * @param {string} tag - HTML tag name
 * @param {Object} attrs - Element attributes
 * @param {...(string|HTMLElement|null)} children - Child elements or text
 * @return {HTMLElement} Created element
 */
export function el(tag, attrs = {}, ...children) {
    const element = document.createElement(tag);

    // Set attributes
    Object.keys(attrs).forEach(key => {
        if (key === 'class' || key === 'className') {
            element.className = attrs[key];
        } else if (key === 'style' && typeof attrs[key] === 'object') {
            Object.assign(element.style, attrs[key]);
        } else if (key.startsWith('on') && typeof attrs[key] === 'function') {
            const eventName = key.substring(2).toLowerCase();
            element.addEventListener(eventName, attrs[key]);
        } else if (key.startsWith('data-')) {
            element.setAttribute(key, attrs[key]);
        } else if (key === 'aria-label' || key === 'aria-expanded' || key === 'aria-hidden') {
            element.setAttribute(key, attrs[key]);
        } else if (attrs[key] !== undefined && attrs[key] !== null && attrs[key] !== false) {
            if (typeof attrs[key] === 'boolean') {
                if (attrs[key]) {
                    element.setAttribute(key, '');
                }
            } else {
                element.setAttribute(key, attrs[key]);
            }
        }
    });

    // Append children
    children.forEach(child => {
        if (child === null || child === undefined || child === false) {
            return;
        }
        if (typeof child === 'string' || typeof child === 'number') {
            element.appendChild(document.createTextNode(String(child)));
        } else if (child instanceof HTMLElement || child instanceof DocumentFragment) {
            element.appendChild(child);
        }
    });

    return element;
}

/**
 * Translate a string using WordPress i18n
 *
 * @param {string} text - Text to translate
 * @param {string} domain - Text domain
 * @return {string} Translated text
 */
export function __(text, domain = 'multilingual-press-zone') {
    if (window.wp && window.wp.i18n) {
        return window.wp.i18n.__(text, domain);
    }
    return text;
}

/**
 * Translate and escape a string
 *
 * @param {string} text - Text to translate
 * @param {string} domain - Text domain
 * @return {string} Translated and escaped text
 */
export function esc_html__(text, domain = 'multilingual-press-zone') {
    const translated = __(text, domain);
    return escapeHtml(translated);
}

/**
 * Escape HTML special characters
 *
 * @param {string} text - Text to escape
 * @return {string} Escaped text
 */
export function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Debounce function calls
 *
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @return {Function} Debounced function
 */
export function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Add multiple event listeners
 *
 * @param {HTMLElement} element - Target element
 * @param {string} events - Space-separated event names
 * @param {Function} handler - Event handler
 */
export function addEvents(element, events, handler) {
    events.split(' ').forEach(event => {
        element.addEventListener(event, handler);
    });
}

/**
 * Remove multiple event listeners
 *
 * @param {HTMLElement} element - Target element
 * @param {string} events - Space-separated event names
 * @param {Function} handler - Event handler
 */
export function removeEvents(element, events, handler) {
    events.split(' ').forEach(event => {
        element.removeEventListener(event, handler);
    });
}

/**
 * Check if element is visible
 *
 * @param {HTMLElement} element - Element to check
 * @return {boolean} True if visible
 */
export function isVisible(element) {
    return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
}

/**
 * Clear all children from an element
 *
 * @param {HTMLElement} element - Element to clear
 */
export function clear(element) {
    if (element) {
        element.innerHTML = '';
    }
}

/**
 * Format a date string for display
 *
 * @param {string|Date} dateStr - Date string or Date object
 * @return {string} Formatted date
 */
export function formatDate(dateStr) {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Format a number with locale-aware thousands separators
 *
 * @param {number|string} num - Number to format
 * @return {string} Formatted number
 */
export function formatNumber(num) {
    if (num === null || num === undefined) return '0';
    const n = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(n)) return '0';
    return n.toLocaleString();
}

/**
 * Get closest parent element matching selector
 *
 * @param {HTMLElement} element - Starting element
 * @param {string} selector - CSS selector
 * @return {HTMLElement|null} Matching parent or null
 */
export function closest(element, selector) {
    return element.closest(selector);
}

/**
 * Query selector helper
 *
 * @param {string} selector - CSS selector
 * @param {HTMLElement} context - Context element (default: document)
 * @return {HTMLElement|null} Found element or null
 */
export function $(selector, context = document) {
    return context.querySelector(selector);
}

/**
 * Query selector all helper
 *
 * @param {string} selector - CSS selector
 * @param {HTMLElement} context - Context element (default: document)
 * @return {Array<HTMLElement>} Array of found elements
 */
export function $$(selector, context = document) {
    return Array.from(context.querySelectorAll(selector));
}

export default {
    el,
    __,
    esc_html__,
    escapeHtml,
    debounce,
    addEvents,
    removeEvents,
    isVisible,
    clear,
    formatDate,
    formatNumber,
    closest,
    $,
    $$
};
